import { motion } from 'motion/react'
import { useId } from 'react'
import { Link, useLocation } from 'react-router'

import type { TenantAccess } from '../api/types'
import { PLATFORM_HOME } from './area'

/**
 * The municipality is the context of everything below the header, so it gets its own block next
 * to the product name: label "Kommune", the name prominent, optionally the logo. Members of
 * several municipalities switch here; platform admins leave via "Alle Kommunen" and switch in
 * the overview of the platform (map „Plattform-Bereich“, ticket 01).
 */
export function TenantContextView({
  tenants,
  current,
  platformAdmin,
  logoUrl,
  onSelect,
}: {
  tenants: readonly TenantAccess[]
  current: TenantAccess | undefined
  platformAdmin: boolean
  logoUrl?: string | null
  onSelect: (id: string) => void
}) {
  const id = useId()
  if (!current && tenants.length < 2) return null
  const several = !platformAdmin && tenants.length > 1
  return (
    <div className="dms-context">
      {logoUrl && <img src={logoUrl} alt="" className="dms-logo" />}
      <div className="flex min-w-0 flex-col">
        <label htmlFor={several ? id : undefined} className="dms-context__label">
          Kommune
        </label>
        {several ? (
          <div className="kern-form-input__select-wrapper dms-context__select">
            <select id={id} className="kern-form-input__select" value={current?.id ?? ''} onChange={(e) => onSelect(e.target.value)}>
              {!current && <option value="">Bitte wählen</option>}
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.active ? '' : ' (deaktiviert)'}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <span className="dms-context__value">{current?.name}</span>
        )}
        {platformAdmin && current && !current.active && (
          <span className="dms-caption">deaktiviert – Mitglieder haben keinen Zugriff</span>
        )}
      </div>
      {platformAdmin && (
        <Link
          to={PLATFORM_HOME}
          title="Übersicht aller Kommunen"
          className="kern-link kern-link--no-visited-state dms-focus ml-8 shrink-0 text-kern-16"
        >
          Alle Kommunen
        </Link>
      )}
    </div>
  )
}

/** Context block of the platform: no municipality, the area itself is the context. */
export function PlatformContext() {
  return (
    <div className="dms-context">
      <div className="flex min-w-0 flex-col">
        <span className="dms-context__label">Bereich</span>
        <span className="dms-context__value">Plattform</span>
      </div>
    </div>
  )
}

const PLATFORM_PAGES = [
  { path: PLATFORM_HOME, label: 'Kommunen' },
  { path: '/plattform/verwaltung', label: 'Verwaltung' },
] as const

/** "Kommunen" covers the overview and the detail pages; the administration is its own page. */
const activePage = (pathname: string) =>
  pathname.startsWith('/plattform/verwaltung') ? '/plattform/verwaltung' : PLATFORM_HOME

export function PlatformNav() {
  const { pathname } = useLocation()
  const active = activePage(pathname)
  return (
    <nav aria-label="Plattform" className="no-print">
      <ul className="flex flex-wrap gap-4 pb-4">
        {PLATFORM_PAGES.map((page) => {
          const current = page.path === active
          return (
            <li key={page.path} className="relative">
              <Link
                to={page.path}
                aria-current={current ? 'page' : undefined}
                className="dms-focus relative flex rounded-kern-default px-12 py-8 text-kern-16 no-underline"
                style={{ color: current ? 'var(--dms-on-action)' : 'var(--dms-text)' }}
              >
                {current && (
                  <motion.span
                    layoutId="platform-nav-active"
                    className="absolute inset-0 rounded-kern-default"
                    style={{ background: 'var(--dms-action)' }}
                    transition={{ type: 'spring', stiffness: 400, damping: 34 }}
                  />
                )}
                <span className="relative whitespace-nowrap">{page.label}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
